'use client';

import { useState, useCallback, useRef } from 'react';
import { getChatCompletion, getStreamingChatCompletion } from '@/lib/ai/chatCompletion';

export function useChat(provider: string, model: string, streaming: boolean = true) {
  const [response, setResponse] = useState('');
  const [fullResponse, setFullResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Abort controller ref — cancel in-flight request when a new one starts
  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(
    async (messages: object[], parameters: object = {}) => {
      // Cancel any in-flight request before starting a new one
      abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setResponse('');
      setFullResponse(streaming ? [] : null);
      setIsLoading(true);
      setError(null);

      try {
        if (streaming) {
          await getStreamingChatCompletion(
            provider,
            model,
            messages,
            (chunk) => {
              // Guard: ignore chunks after abort
              if (controller.signal.aborted) return;
              setFullResponse((prev: any[]) => [...(prev ?? []), chunk]);
              const content = chunk?.choices?.[0]?.delta?.content;
              if (content) setResponse((prev) => prev + content);
            },
            () => {
              if (controller.signal.aborted) return;
              setIsLoading(false);
            },
            (err) => {
              if (controller.signal.aborted) return;
              setError(err);
              setIsLoading(false);
            },
            parameters,
            controller.signal
          );
        } else {
          const result = await getChatCompletion(provider, model, messages, parameters);
          if (controller.signal.aborted) return;
          setFullResponse(result);
          setResponse(result?.choices?.[0]?.message?.content || '');
          setIsLoading(false);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setIsLoading(false);
      }
    },
    [provider, model, streaming, abort]
  );

  return { response, fullResponse, isLoading, error, sendMessage, abort };
}
